// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductImagesBadges, {
    ProductImagesBadgesStyles,
} from "@eisinc/App/Components/CustomBadges/ProductImagesBadges";
import Button, { ButtonPresentationProps } from "@eisinc/MobiusOverrides/Button";
import { SelectPresentationProps } from "@eisinc/MobiusOverrides/Select";
import { TextFieldProps } from "@eisinc/MobiusOverrides/TextField";
import ProductAddToCartButton from "@eisinc/Overrides/Components/ProductAddToCartButton";
import { ProductAvailabilityStyles } from "@eisinc/Overrides/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@eisinc/Overrides/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@eisinc/Overrides/Components/ProductDescription";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@eisinc/Overrides/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@eisinc/Overrides/Components/ProductPrice";
import { ProductQuantityBreakPricingStyles } from "@eisinc/Overrides/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered, { UnitOfMeasureData } from "@eisinc/Overrides/Components/ProductQuantityOrdered";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    isWishListLineDeactivated,
    isWishListLineDiscontinued,
    isWishListLineRestricted,
} from "@insite/client-framework/Store/Data/WishListLines/WishListLinesSelectors";
import setWishListLineIsSelected from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetWishListLineIsSelected";
import updateQuantity from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateQuantity";
import updateUnitOfMeasure from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateUnitOfMeasure";
import updateWishListLineQuantities from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateWishListLineQuantities";
import translate from "@insite/client-framework/Translate";
import { ProductModel, WishListLineModel, WishListModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges, { ProductImageBadgesStyles } from "@insite/content-library/Components/ProductImageBadges";
import ProductTextBadges, { ProductTextBadgesStyles } from "@insite/content-library/Components/ProductTextBadges";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import MyListDetailsDiscontinuedProductListLine from "@insite/content-library/Widgets/MyListsDetails/MyListDetailsDiscontinuedProductListLine";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import Clickable from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { HiddenProps } from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Check from "@insite/mobius/Icons/Check";
import RefreshCw from "@insite/mobius/Icons/RefreshCw";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import cloneDeep from "lodash/cloneDeep";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    wishList: WishListModel;
    wishListLine: WishListLineModel;
    productInfo: ProductInfo;
    onDeleteClick: (wishListLine: WishListLineModel) => void;
    onEditNotesClick: (wishListLine: WishListLineModel) => void;
    showImageBadges: boolean;
    maximumNumberOfImageBadges: number;
    showTextBadges: boolean;
    maximumNumberOfTextBadges: number;
    onViewReplacementProduct?: (wishListLine: WishListLineModel) => void;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    isSelected: !!state.pages.myListDetails.selectedWishListLineIds.find(o => o === ownProps.wishListLine.id),
    settingsCollection: getSettingsCollection(state),
    isQuantityUpdated: state.pages.myListDetails.wishListLinesWithUpdatedQuantity[ownProps.wishListLine.id],
});

const mapDispatchToProps = {
    setWishListLineIsSelected,
    updateUnitOfMeasure,
    updateQuantity,
    updateWishListLineQuantities,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface MyListsDetailsProductListLineStyles {
    editNotesLink?: LinkPresentationProps;
    lineInnerContainer?: GridContainerProps;
    selectedCheckboxHidden?: HiddenProps;
    imageGridItem?: GridItemProps;
    selectedCheckbox?: CheckboxPresentationProps;
    imageWrapper?: InjectableCss;
    editNotesHidden?: HiddenProps;
    productImageStyles?: ProductImageStyles;
    descriptionGridItem?: GridItemProps;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    notesStyles?: SmallHeadingAndTextStyles;
    overflowMenuGridItem?: GridItemProps;
    partNumbersHidden?: HiddenProps;
    partNumbersGridItem?: GridItemProps;
    priceAndAvailabilityGridItem?: GridItemProps;
    priceAndAvailabilityInnerContainer?: GridContainerProps;
    purchasedDateGridItem?: GridItemProps;
    purchasedDateLabelText?: TypographyPresentationProps;
    purchasedDateText?: TypographyPresentationProps;
    priceGridItem?: GridItemProps;
    productPriceStyles?: ProductPriceStyles;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    uomAndQuantityGridItem?: GridItemProps;
    uomAndQuantityInnerContainer?: GridContainerProps;
    uomGridItem?: GridItemProps;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    quantityGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    availabilityGridItem?: GridItemProps;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    notesHidden?: HiddenProps;
    notesGridItem?: GridItemProps;
    buttonsGridItem?: GridItemProps;
    addToCartButton?: ButtonPresentationProps;
    deleteLinkHidden?: HiddenProps;
    deleteLink?: LinkPresentationProps;
    updateQuantityWrapper?: InjectableCss;
    updateQuantityLink?: LinkPresentationProps;
    quantityUpdatedIcon?: IconPresentationProps;
    quantityUpdatedText?: TypographyPresentationProps;
    restrictedDescriptionGridItem?: GridItemProps;
    restrictedDescriptionText?: TypographyPresentationProps;
    restrictedMessageText?: TypographyPresentationProps;
    restrictedRemoveItemLink?: LinkPresentationProps;
    textBadgeStyles?: ProductTextBadgesStyles;
    imageBadgeStyles?: ProductImageBadgesStyles;
    // XNG-Changes
    actionsWrapper?: InjectableCss;
    actionsWrapperInner?: InjectableCss;
    container?: InjectableCss;
    infoWrapper?: InjectableCss;
    infoContainer?: InjectableCss;
    imageWrapperDetails?: InjectableCss;
    listInfoWrapper?: InjectableCss;
    numbersBrandWrapper?: InjectableCss;
    brandStyles?: ProductBrandStyles;
    badgesAvailabilityWrapper?: InjectableCss;
    badgesContainerStyle?: InjectableCss;
    listAvailabilityStyles?: ProductAvailabilityStyles;
    priceWrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
    quantityOrdered?: TextFieldProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    price?: ProductPriceStyles;
    qtyWrapper?: InjectableCss;
    uomWrapper?: InjectableCss;
    atcWrapper?: InjectableCss;
    chooseOptionsClickableStyles?: InjectableCss;
    discontinuedProductDescription?: TypographyPresentationProps;
    customImagesBadgesStyle?: ProductImagesBadgesStyles;
}

export const productListLineStyles: MyListsDetailsProductListLineStyles = {
    editNotesLink: {
        typographyProps: { size: 12 },
        css: css`
            margin-top: 5px;
        `,
    },
    lineInnerContainer: {
        gap: 10,
    },
    selectedCheckboxHidden: { below: "md" },
    imageGridItem: {
        width: [3, 3, 2, 2, 1],
        printWidth: 1,
    },
    selectedCheckbox: {
        sizeVariant: "small",
        css: css`
            @media print {
                display: none;
            }
            margin-right: 10px;
        `,
    },
    imageWrapper: {
        css: css`
            @media print {
                max-height: 83px;
                max-width: 83px;
            }
            width: 100%;
            min-width: 0;
            flex-direction: column;
        `,
    },
    editNotesHidden: { below: "md" },
    descriptionGridItem: {
        width: [8, 8, 4, 4, 4],
        printWidth: 6,
        css: css`
            flex-direction: column;
        `,
    },
    productPartNumbersStyles: {
        erpNumberGridItem: {
            css: css`
                flex-direction: column;
            `,
        },
        erpNumberLabelText: { variant: "legend" },
        customerNameGridItem: {
            css: css`
                flex-direction: column;
            `,
        },
        customerNameLabelText: { variant: "legend" },
        manufacturerItemGridItem: {
            css: css`
                flex-direction: column;
            `,
        },
        manufacturerItemLabelText: { variant: "legend" },
    },
    overflowMenuGridItem: {
        width: [1, 1, 0, 0, 0],
        printWidth: 0,
    },
    partNumbersHidden: {
        below: "md",
        css: css`
            @media print {
                display: block;
            }
            width: 100%;
        `,
    },
    partNumbersGridItem: { width: 12 },
    priceAndAvailabilityGridItem: {
        width: [12, 12, 4, 4, 5],
        printWidth: 5,
    },
    priceAndAvailabilityInnerContainer: { gap: 10 },
    purchasedDateGridItem: {
        width: [12, 12, 12, 12, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    purchasedDateLabelText: {
        variant: "p",
        transform: "uppercase",
        weight: 600,
        css: css`
            @media print {
                font-size: 12px;
            }
        `,
    },
    priceGridItem: {
        width: [12, 12, 12, 12, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    productPriceStyles: {
        priceLabelText: {
            variant: "p",
            transform: "uppercase",
            weight: 600,
            css: css`
                @media print {
                    font-size: 12px;
                }
            `,
        },
    },
    quantityBreakPricing: {
        viewLink: { typographyProps: { size: 12 } },
    },
    uomAndQuantityGridItem: {
        width: [12, 12, 12, 12, 7],
        printWidth: 7,
    },
    uomAndQuantityInnerContainer: {
        gap: 10,
        css: css`
            @media print {
                input,
                select {
                    height: 18px !important;
                    font-size: 11px !important;
                }
            }
        `,
    },
    uomGridItem: { width: 9, printWidth: 6 },
    quantityGridItem: { width: 3, printWidth: 6 },
    productQuantityOrderedStyles: {
        labelProps: {
            css: css`
                @media print {
                    font-size: 12px;
                }
            `,
        },
        cssOverrides: {
            inputSelect: css`
                @media print {
                    width: 100px;
                }
            `,
        },
    },
    productUnitOfMeasureSelectStyles: {
        labelProps: {
            css: css`
                @media print {
                    font-size: 12px;
                }
            `,
        },
        iconProps: {
            css: css`
                @media print {
                    display: none;
                }
            `,
        },
    },
    availabilityGridItem: { width: 12 },
    buttonsGridItem: {
        width: [12, 12, 2, 2, 2],
        printWidth: 0,
        css: css`
            flex-direction: column;
            align-items: center;
        `,
    },
    notesHidden: {
        above: "sm",
        css: css`
            @media print {
                display: none;
            }
            width: 100%;
        `,
    },
    notesGridItem: { width: 12 },
    addToCartButton: {
        css: css`
            width: 100%;
        `,
    },
    deleteLinkHidden: {
        below: "md",
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
            align-items: center;
        `,
    },
    deleteLink: {
        typographyProps: { size: 12 },
        css: css`
            margin-top: 10px;
        `,
    },
    updateQuantityWrapper: {
        css: css`
            display: flex;
            margin-top: 10px;
        `,
    },
    updateQuantityLink: {
        typographyProps: { size: 12 },
        icon: {
            iconProps: {
                src: RefreshCw,
                size: 12,
            },
        },
    },
    quantityUpdatedIcon: {
        src: Check,
        size: 16,
        css: css`
            margin-right: 5px;
        `,
    },
    quantityUpdatedText: {
        size: 12,
    },

    textBadgeStyles: {
        container: {
            css: css`
                width: 60%;
                margin-bottom: 5px;
            `,
        },
    },
    imageBadgeStyles: {
        container: {
            css: css`
                width: 60%;
                margin-bottom: 5px;
            `,
        },
        imageItem: {
            css: css`
                margin: 5px 0;
            `,
        },
    },

    restrictedDescriptionGridItem: {
        width: [12, 12, 6, 6, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    restrictedMessageText: { color: "warning" },
};

const styles = productListLineStyles;

const MyListsDetailsProductListLine: React.FC<Props> = ({
    wishList,
    wishListLine,
    productInfo,
    isSelected,
    settingsCollection,
    isQuantityUpdated,
    onDeleteClick,
    onEditNotesClick,
    updateWishListLineQuantities,
    setWishListLineIsSelected,
    updateUnitOfMeasure,
    updateQuantity,
    showImageBadges,
    showTextBadges,
    maximumNumberOfImageBadges,
    maximumNumberOfTextBadges,
    onViewReplacementProduct,
}) => {
    const product = mapWishListLineToProduct(wishListLine);
    const unitOfMeasuresData: UnitOfMeasureData[] = JSON.parse(wishListLine.properties.unitOfMeasureData || "[]");
    const selectedUOM = unitOfMeasuresData.filter(uom => uom.UnitOfMeasure === wishListLine.unitOfMeasure);
    const selectChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setWishListLineIsSelected({ wishListLineId: wishListLine.id, isSelected: value });
    };

    const onUpdateQuantityClick = () => {
        updateWishListLineQuantities({ reloadWishListLines: true });
    };

    const canEditWishList = (wishList.allowEdit || !wishList.isSharedList) && !wishList.isAutogenerated;

    const { productSettings } = settingsCollection;
    const productContext: ProductContextModel = {
        product,
        productInfo,
        onQtyOrderedChanged: qtyOrdered => {
            updateQuantity({ wishListLineId: wishListLine.id, qtyOrdered });
        },
        onUnitOfMeasureChanged: unitOfMeasure => {
            updateUnitOfMeasure({ wishListLineId: wishListLine.id, unitOfMeasure });
        },
    };

    if (isWishListLineDiscontinued(wishListLine, productInfo) && wishListLine.isActive) {
        return (
            <MyListDetailsDiscontinuedProductListLine
                wishList={wishList}
                wishListLine={wishListLine}
                productContext={productContext}
                isSelected={isSelected}
                selectChangeHandler={selectChangeHandler}
                onDeleteClick={onDeleteClick}
                onViewReplacementProduct={onViewReplacementProduct}
            />
        );
    }

    if (isWishListLineRestricted(wishListLine, productInfo) || isWishListLineDeactivated(wishListLine)) {
        return (
            <StyledWrapper {...styles.container} className="wishlist-restricted-line-grid-container">
                <StyledWrapper {...styles.imageWrapper} />
                <StyledWrapper {...styles.infoWrapper}>
                    <StyledWrapper {...styles.infoContainer}>
                        <StyledWrapper {...styles.descriptionGridItem}>
                            <Typography {...styles.discontinuedProductDescription}>
                                {wishListLine.shortDescription}
                            </Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.listInfoWrapper}>
                            <StyledWrapper {...styles.numbersBrandWrapper}>
                                <StyledWrapper {...styles.partNumbersGridItem}>
                                    <ProductPartNumbers
                                        productNumber={product.productNumber}
                                        customerProductNumber={product.customerProductNumber}
                                        manufacturerItem={product.manufacturerItem}
                                        specNumber={product.properties.specNumber || ""}
                                        extendedStyles={styles.productPartNumbersStyles}
                                        showLabel={true}
                                    />
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper>
                            <Typography {...styles.restrictedMessageText} data-test-selector="restrictedMessage">
                                {isWishListLineRestricted(wishListLine, productInfo) &&
                                    !isWishListLineDeactivated(wishListLine) && (
                                        <>{siteMessage("Lists_Item_Not_Displayed_Due_To_Restrictions")}</>
                                    )}
                                {isWishListLineDeactivated(wishListLine) && (
                                    <>{siteMessage("Lists_Item_Not_Displayed_Due_To_Deactivation")}</>
                                )}
                            </Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.actionsWrapper}>
                    <StyledWrapper {...styles.actionsWrapperInner}>
                        {canEditWishList && (
                            <>
                                <StyledWrapper
                                    css={css`
                                        margin-top: 20px;
                                        display: flex;
                                        justify-content: space-between;
                                    `}
                                >
                                    <Link
                                        onClick={() => onDeleteClick(wishListLine)}
                                        data-test-selector="removeRestricted"
                                    >
                                        {translate("Remove item")}
                                    </Link>
                                </StyledWrapper>
                            </>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    const badges = (product.properties["badges"] && JSON.parse(product.properties["badges"])) || [];

    return (
        <ProductContext.Provider value={productContext}>
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.imageWrapper}>
                    <StyledWrapper {...styles.imageWrapperDetails}>
                        <Checkbox
                            checked={isSelected}
                            onChange={selectChangeHandler}
                            hiddenIconText={translate("Select")}
                            data-test-selector="myListDetails_selectRowCheckbox"
                            {...styles.selectedCheckbox}
                        />
                        <StyledWrapper>
                            <ProductImage product={productContext} />
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.infoWrapper}>
                    <StyledWrapper {...styles.infoContainer}>
                        <StyledWrapper {...styles.descriptionGridItem}>
                            <ProductDescription
                                product={productContext}
                                extendedStyles={styles.productDescriptionStyles}
                            />
                        </StyledWrapper>
                        <StyledWrapper {...styles.listInfoWrapper}>
                            <StyledWrapper {...styles.numbersBrandWrapper}>
                                <StyledWrapper {...styles.partNumbersGridItem}>
                                    <ProductPartNumbers
                                        productNumber={product.productNumber}
                                        customerProductNumber={product.customerProductNumber}
                                        manufacturerItem={product.manufacturerItem}
                                        specNumber={product.properties.specNumber || ""}
                                        extendedStyles={styles.productPartNumbersStyles}
                                        showLabel={true}
                                    />
                                </StyledWrapper>
                                {product.brand && (
                                    <StyledWrapper>
                                        <ProductBrand
                                            brand={product.brand}
                                            extendedStyles={styles.brandStyles}
                                            showLogo={false}
                                            showLabel={true}
                                        />
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                            <StyledWrapper {...styles.badgesAvailabilityWrapper}>
                                {settingsCollection.productSettings.showInventoryAvailability && (
                                    <StyledWrapper
                                        className="wishlist-line-availability-grid-item"
                                        css={css`
                                            margin-bottom: 11px;
                                        `}
                                    >
                                        <ProductContextAvailability extendedStyles={styles.listAvailabilityStyles} />
                                    </StyledWrapper>
                                )}
                                {badges?.length > 0 && (
                                    <StyledWrapper {...styles.badgesContainerStyle} className="info-list-view-badges">
                                        <ProductImagesBadges
                                            product={product}
                                            badges={badges}
                                            extendedStyles={styles.customImagesBadgesStyle}
                                        />
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper className="wishlist-line-notes-grid-item">
                            {wishListLine.notes && (
                                <SmallHeadingAndText
                                    heading={translate("Notes")}
                                    text={wishListLine.notes}
                                    extendedStyles={styles.notesStyles}
                                />
                            )}
                        </StyledWrapper>
                        {showTextBadges && textBadges && (
                            <ProductTextBadges
                                badgeList={textBadges}
                                maxBadges={maximumNumberOfTextBadges ?? 4}
                                extendedStyles={styles.textBadgeStyles}
                            />
                        )}
                        {showImageBadges && imageBadges && (
                            <ProductImageBadges
                                badgeList={imageBadges}
                                maxBadges={maximumNumberOfImageBadges ?? 4}
                                extendedStyles={styles.imageBadgeStyles}
                                numberOfColumns={4}
                            />
                        )}
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.actionsWrapper}>
                    <StyledWrapper {...styles.actionsWrapperInner}>
                        {wishList.isAutogenerated && (
                            <StyledWrapper {...styles.purchasedDateGridItem}>
                                <Typography
                                    {...styles.purchasedDateLabelText}
                                    className="wishlist-line-purchased-date-label-text"
                                >
                                    {translate("Purchased Date")}
                                </Typography>
                                <Typography
                                    {...styles.purchasedDateText}
                                    className="wishlist-line-purchased-date-text"
                                    data-test-selector="purchasedDate"
                                >
                                    <LocalizedDateTime dateTime={wishListLine.createdOn} />
                                </Typography>
                            </StyledWrapper>
                        )}
                        <StyledWrapper {...styles.priceWrapper} className="wishlist-line-price-grid-container">
                            {!productContext.product.isVariantParent && (
                                <>
                                    <ProductPrice
                                        product={productContext}
                                        showLabel={false}
                                        showSavings={true}
                                        showSavingsAmount={productSettings.showSavingsAmount}
                                        showSavingsPercent={productSettings.showSavingsPercent}
                                    />
                                </>
                            )}
                        </StyledWrapper>
                        {!productContext.product.isVariantParent ? (
                            <StyledWrapper {...styles.addToCartWrapper}>
                                {(productContext.product.unitOfMeasures as any).length > 1 && (
                                    <StyledWrapper {...styles.uomWrapper}>
                                        <ProductUnitOfMeasureSelect
                                            extendedStyles={styles.unitOfMeasureSelect}
                                            labelOverride=""
                                            disabled={!canEditWishList}
                                        />
                                    </StyledWrapper>
                                )}
                                <StyledWrapper {...styles.qtyWrapper} className="wishlist-line-quantity-grid-item">
                                    <ProductQuantityOrdered extendedStyles={styles.quantityOrdered} labelOverride="" />
                                </StyledWrapper>
                                <StyledWrapper {...styles.atcWrapper} className="wishlist-line-buttons-grid-item">
                                    <ProductAddToCartButton
                                        notes={wishListLine.notes}
                                        data-test-selector={`addToCart${productContext.product.id}`}
                                        extendedStyles={styles.addToCartButton}
                                    />
                                </StyledWrapper>
                            </StyledWrapper>
                        ) : (
                            <Clickable
                                href={productContext.productInfo.productDetailPath}
                                {...styles.chooseOptionsClickableStyles}
                                className="list-view-options-button"
                            >
                                <Button variant="primary">{translate("Select Options")}</Button>
                            </Clickable>
                        )}
                        <StyledWrapper
                            css={css`
                                margin-top: auto;
                            `}
                        >
                            {canEditWishList && (
                                <>
                                    <StyledWrapper>
                                        {productInfo.qtyOrdered > 0 &&
                                            wishListLine.qtyOrdered !== productInfo.qtyOrdered &&
                                            (selectedUOM.length
                                                ? wishListLine.qtyOrdered >=
                                                  parseFloat(selectedUOM[0].MinimumOrderQuantity)
                                                : true) && (
                                                <Link
                                                    className="wishlist-line-update-quantity-link"
                                                    onClick={() => onUpdateQuantityClick()}
                                                >
                                                    {translate("Update QTY")}
                                                </Link>
                                            )}
                                        {isQuantityUpdated && wishListLine.qtyOrdered === productInfo.qtyOrdered && (
                                            <>
                                                <Icon className="wishlist-line-quantity-updated-icon" />
                                                <Typography className="wishlist-line-quantity-updated-text">
                                                    {translate("QTY updated")}
                                                </Typography>
                                            </>
                                        )}
                                    </StyledWrapper>
                                    <StyledWrapper
                                        css={css`
                                            margin-top: 20px;
                                            display: flex;
                                            justify-content: space-between;
                                        `}
                                    >
                                        <Link
                                            onClick={() => onEditNotesClick(wishListLine)}
                                            data-test-selector="myListDetails_addOrEditNote"
                                        >
                                            {translate(`${wishListLine.notes ? "Edit" : "Add"} Notes`)}
                                        </Link>
                                        <Link
                                            className="wishlist-line-delete-link"
                                            onClick={() => onDeleteClick(wishListLine)}
                                            data-test-selector="deleteListItem"
                                        >
                                            {translate("Delete")}
                                        </Link>
                                    </StyledWrapper>
                                </>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </ProductContext.Provider>
    );
};

const mapWishListLineToProduct = (wishListLine: WishListLineModel) => {
    const product = cloneDeep(wishListLine) as any as ProductModel;
    product.id = wishListLine.productId;
    product.productNumber = wishListLine.erpNumber;
    product.customerProductNumber = wishListLine.customerName;
    product.productTitle = wishListLine.shortDescription;
    product.customerProductNumber = wishListLine.customerName;
    product.unitOfMeasures =
        wishListLine?.productUnitOfMeasures?.map(u => ({
            id: u.productUnitOfMeasureId,
            ...u,
        })) || null;

    return product;
};

export default connect(mapStateToProps, mapDispatchToProps)(MyListsDetailsProductListLine);
